import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ArrowLeftRight,
  MessageCircle,
  Clock,
  Upload,
  FileText,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";
import ComptaDrawer from "../ComptaDrawer";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { formTabButtonActiveClass, formTabButtonClass, formTabsListClass } from "@/lib/ui/formFieldClasses";
import CommentairesTab from "../CommentairesTab";
import TransactionEcrituresTab from "./TransactionEcrituresTab";
import TransactionReconciliationTab from "./TransactionReconciliationTab";
import { supabase } from "../../../lib/supabase";
import { resolveFileOrPieceUrl } from "../../../lib/piecesJustificativesStorage";
import { countCommentaires } from "../../../lib/commentaires";
import { createFournisseur, findProbableDoublonFournisseur, resolveFournisseurFkPourFactureAchat } from "../../../lib/fournisseurs";
import { genererEcritureFactureAchatValidee } from "../../../lib/comptable";
import {
  updateTransactionJustificatifCompta,
  normalizeJustificatifStatut,
  type JustificatifStatut,
} from "../../../lib/transactions";
import { formatMontantFCFA } from "@/lib/formatMontant";

const fmt = (n: number) => formatMontantFCFA(n);
const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—");

export interface TransactionComptaDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: {
    id: string;
    date: string;
    libelle: string;
    montantTTC: number;
    type: "entree" | "sortie";
    statut?: string;
    statut_justification?: string | null;
    suggestedTier?: string | null;
    modeTraitement?: string | null;
    justificatif_requis?: boolean | null;
    justificatif_statut?: JustificatifStatut | null;
    piece_nom?: string | null;
    piece_url?: string | null;
    piece_type?: string | null;
    piece_taille?: number | null;
    /** Contexte rapprochement (achat / vente) — pour le badge, fourni par la vue liste */
    context?: "direct" | "achat" | "vente_encaissement" | "vente_remboursement" | null;
  };
  dossierId: string;
  onRapprochement?: () => void;
  onCommentairesChanged?: (transactionId: string) => void;
  /** Met à jour la transaction en mémoire après upload / remplacement de pièce */
  onPieceUpdated?: (patch: {
    piece_nom: string | null;
    piece_url: string | null;
    piece_type: string | null;
    piece_taille: number | null;
    justificatif_statut?: JustificatifStatut | null;
  }) => void;
  onJustificatifUpdated?: (patch: {
    justificatif_requis: boolean;
    justificatif_statut: JustificatifStatut;
    statut_justification?: "justifiee" | "non_justifiee";
  }) => void;
}

const TABS = [
  { id: "ecritures" as const, label: "Écritures", icon: BookOpen },
  { id: "reconciliation" as const, label: "Réconciliation", icon: ArrowLeftRight },
  { id: "commentaires" as const, label: "Commentaires", icon: MessageCircle },
  { id: "activite" as const, label: "Activité", icon: Clock },
];

function isPdfType(pieceType: string | null | undefined): boolean {
  return (pieceType || "").toLowerCase().includes("pdf");
}

function isImageType(pieceType: string | null | undefined): boolean {
  const t = (pieceType || "").toLowerCase();
  return t.startsWith("image/");
}

function safeStorageFileName(file: File): string {
  const sanitizedName = file.name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .toLowerCase();
  return `${Date.now()}_${sanitizedName || "piece"}`;
}

interface PieceJustificativeSectionProps {
  dossierId: string;
  transactionId: string;
  txDate: string;
  txMontantTTC: number;
  piece_nom: string | null;
  piece_url: string | null;
  piece_type: string | null;
  piece_taille: number | null;
  tresorerieDirecte?: boolean;
  onPieceUpdated?: TransactionComptaDrawerProps["onPieceUpdated"];
  onCreatedAndLinked?: () => void;
  showToast: (type: "success" | "error", message: string) => void;
}

function TransactionPieceJustificativeSection({
  dossierId,
  transactionId,
  txDate,
  txMontantTTC,
  piece_nom,
  piece_url,
  piece_type,
  tresorerieDirecte,
  onPieceUpdated,
  onCreatedAndLinked,
  showToast,
}: PieceJustificativeSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [creatingFacture, setCreatingFacture] = useState(false);
  const [creatingFournisseur, setCreatingFournisseur] = useState(false);
  const [resolvedPieceUrl, setResolvedPieceUrl] = useState<string | null>(null);
  const [factureDraft, setFactureDraft] = useState<{
    fournisseur_nom: string;
    fournisseur_fk: string | null;
    numero: string;
    date_emission: string;
    montant_ht: number;
    montant_tva: number;
    storage_path: string;
    file_url: string;
    type_document: string;
  } | null>(null);

  const ttcForm = Math.max(0, Number(factureDraft?.montant_ht || 0) + Number(factureDraft?.montant_tva || 0));
  const fournisseurConnu = Boolean(factureDraft?.fournisseur_fk);

  useEffect(() => {
    let cancelled = false;
    const raw = piece_url?.trim();
    if (!raw) {
      setResolvedPieceUrl(null);
      return;
    }
    void resolveFileOrPieceUrl(raw).then((u) => {
      if (!cancelled) setResolvedPieceUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [piece_url]);

  const triggerFilePick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const path = `${dossierId}/${transactionId}/${safeStorageFileName(file)}`;
    console.log("[piece-first] upload path:", path);
    const contentType = file.type || "application/octet-stream";

    setUploading(true);
    try {
      const { data: uploadData, error: upErr } = await supabase.storage.from("factures-achat").upload(path, file, {
        upsert: true,
        contentType,
      });
      if (upErr) throw upErr;
      const uploadedPath = uploadData?.path;
      console.log("[piece-first] storage path after upload:", uploadedPath);
      if (!uploadedPath) {
        throw new Error("Upload réussi mais path storage introuvable.");
      }

      const patch = {
        piece_nom: file.name,
        piece_url: uploadedPath,
        piece_type: contentType,
        piece_taille: file.size,
      };

      const dbPatch = tresorerieDirecte ? { ...patch, justificatif_statut: "fourni" as const } : patch;
      const { error: dbErr } = await supabase.from("transactions").update(dbPatch).eq("id", transactionId);
      if (dbErr) throw dbErr;

      onPieceUpdated?.({
        piece_nom: patch.piece_nom,
        piece_url: patch.piece_url,
        piece_type: patch.piece_type,
        piece_taille: patch.piece_taille,
        ...(tresorerieDirecte ? { justificatif_statut: "fourni" as const } : {}),
      });

      setOcrLoading(true);
      const { data: publicData } = supabase.storage.from("factures-achat").getPublicUrl(uploadedPath);
      let extracted: {
        fournisseur_nom?: string;
        numero?: string;
        date_emission?: string;
        montant_ht?: number;
        montant_tva?: number;
        montant_ttc?: number;
      } | null = null;
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;
        console.info("[piece-first] OCR invoke payload", {
          function: "ocr-facture-achat",
          bucket: "factures-achat",
          storage_path: uploadedPath,
          dossier_id: dossierId,
          has_token: Boolean(token),
        });
        const invoke = await supabase.functions.invoke("ocr-facture-achat", {
          body: { storage_path: uploadedPath, dossier_id: dossierId },
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        });
        if (invoke.error) {
          console.error("[piece-first] OCR invoke error", {
            message: invoke.error.message,
            name: invoke.error.name,
            context: { storage_path: uploadedPath, dossier_id: dossierId },
          });
          throw invoke.error;
        }
        if (!invoke.data?.ok) {
          console.error("[piece-first] OCR response not ok", {
            data: invoke.data,
            context: { storage_path: uploadedPath, dossier_id: dossierId },
          });
        }
        if (invoke.data?.ok && invoke.data?.extracted) extracted = invoke.data.extracted;
      } catch (ocrErr) {
        console.error("[piece-first] OCR catch error", {
          error: ocrErr,
          context: { storage_path: uploadedPath, dossier_id: dossierId },
        });
        showToast("error", "OCR indisponible. Vous pouvez saisir les données manuellement.");
      } finally {
        setOcrLoading(false);
      }

      const fournisseurNom = (extracted?.fournisseur_nom || "").trim() || "À compléter";
      let fournisseurFk: string | null = null;
      try {
        fournisseurFk = await resolveFournisseurFkPourFactureAchat(dossierId, { nom: fournisseurNom });
      } catch {
        fournisseurFk = null;
      }
      const ttc = Number(extracted?.montant_ttc) || Number(extracted?.montant_ht || 0) + Number(extracted?.montant_tva || 0);
      setFactureDraft({
        fournisseur_nom: fournisseurNom,
        fournisseur_fk: fournisseurFk,
        numero: (extracted?.numero || file.name.replace(/\.[^/.]+$/, "")).trim(),
        date_emission: (extracted?.date_emission || txDate || new Date().toISOString().slice(0, 10)).slice(0, 10),
        montant_ht: Number(extracted?.montant_ht || 0),
        montant_tva: Number(extracted?.montant_tva || Math.max(0, ttc - Number(extracted?.montant_ht || 0))),
        storage_path: uploadedPath,
        file_url: publicData.publicUrl || uploadedPath,
        type_document: "facture",
      });
      showToast("success", "Pièce importée. Vérifiez les données OCR pour créer la facture.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Échec de l’upload.";
      showToast("error", msg);
    } finally {
      setUploading(false);
    }
  };

  const hasPiece = Boolean(piece_url && String(piece_url).trim());
  const displayUrl = resolvedPieceUrl ?? (piece_url?.trim() || null);

  const handleDraftChange = (patch: Partial<NonNullable<typeof factureDraft>>) => {
    setFactureDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const handleResolveSupplier = async () => {
    if (!factureDraft) return;
    const nom = factureDraft.fournisseur_nom.trim();
    if (!nom) {
      handleDraftChange({ fournisseur_fk: null });
      return;
    }
    const hit = await findProbableDoublonFournisseur(dossierId, { nom });
    handleDraftChange({ fournisseur_fk: hit?.id ?? null });
  };

  const handleCreateSupplier = async () => {
    if (!factureDraft) return;
    const nom = factureDraft.fournisseur_nom.trim();
    if (!nom) {
      showToast("error", "Nom fournisseur requis.");
      return;
    }
    setCreatingFournisseur(true);
    try {
      const created = await createFournisseur(dossierId, { nom });
      handleDraftChange({ fournisseur_fk: created.supplier.id, fournisseur_nom: created.supplier.nom });
      showToast("success", "Fournisseur créé.");
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Impossible de créer le fournisseur.");
    } finally {
      setCreatingFournisseur(false);
    }
  };

  const handleCreateFactureAndLink = async () => {
    if (!factureDraft) return;
    const montantHt = Number(factureDraft.montant_ht) || 0;
    const montantTva = Number(factureDraft.montant_tva) || 0;
    const montantTtc = Math.max(0, montantHt + montantTva);
    if (!factureDraft.fournisseur_nom.trim()) {
      showToast("error", "Le fournisseur est requis.");
      return;
    }
    if (!dossierId?.trim()) {
      showToast("error", "Dossier actif introuvable. Impossible de créer la facture.");
      return;
    }
    setCreatingFacture(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        showToast("error", "Session expirée. Reconnectez-vous puis réessayez.");
        return;
      }

      // Prend le dossier réel de la transaction pour éviter toute divergence de contexte UI.
      let effectiveDossierId = dossierId.trim();
      try {
        const { data: txRow, error: txErr } = await supabase
          .from("transactions")
          .select("id, dossier_id")
          .eq("id", transactionId)
          .single();
        if (txErr) throw txErr;
        const txDossierId = (txRow as { dossier_id?: string | null } | null)?.dossier_id?.trim();
        if (txDossierId) {
          if (txDossierId !== effectiveDossierId) {
            console.warn("[piece-first] dossier mismatch, using transaction dossier_id", {
              ui_dossier_id: effectiveDossierId,
              transaction_dossier_id: txDossierId,
              transaction_id: transactionId,
            });
          }
          effectiveDossierId = txDossierId;
        }
      } catch (txLookupErr) {
        console.warn("[piece-first] transaction dossier lookup failed, fallback to ui dossier_id", {
          transaction_id: transactionId,
          ui_dossier_id: effectiveDossierId,
          error: txLookupErr,
        });
      }

      const factureInsertPayload = {
        dossier_id: effectiveDossierId,
        fournisseur_nom: factureDraft.fournisseur_nom.trim(),
        fournisseur_fk: factureDraft.fournisseur_fk,
        numero: factureDraft.numero.trim() || null,
        date_emission: factureDraft.date_emission || null,
        montant_ht: montantHt,
        montant_tva: montantTva,
        montant_ttc: montantTtc,
        restant_du_ttc: montantTtc,
        statut: "payee",
        source: "transaction_flow",
        file_url: factureDraft.file_url,
        metadata: {
          type_document: factureDraft.type_document,
          storage_path: factureDraft.storage_path,
        },
      };
      const {
        data: { user },
      } = await supabase.auth.getUser();
      console.log("[piece-first] auth user:", user?.id);
      console.log("[piece-first] dossier_id used:", effectiveDossierId);
      console.log("[piece-first] INSERT payload:", {
        dossier_id: effectiveDossierId,
        source: "transaction_flow",
        statut: "a_payer",
      });
      console.info("[piece-first] INSERT factures_achat payload", {
        ...factureInsertPayload,
        auth_user_id: session.user.id,
      });

      const { data: facture, error: factureErr } = await supabase
        .from("factures_achat")
        .insert(factureInsertPayload)
        .select("id, montant_ttc")
        .single();
      if (factureErr) throw factureErr;
      if (!facture?.id) throw new Error("Facture non créée.");

      const montantRapproche = Math.min(Number(facture.montant_ttc) || 0, Math.abs(Number(txMontantTTC) || 0));
      // NOTE: schéma actuel = {facture_achat_id, transaction_id, montant}; pas de colonne dossier_id.
      const rapprochementPayload = {
        facture_achat_id: facture.id,
        transaction_id: transactionId,
        montant: montantRapproche,
      };
      console.info("[piece-first] INSERT facture_achat_rapprochements payload", {
        ...rapprochementPayload,
        dossier_id_context: effectiveDossierId,
        auth_user_id: session.user.id,
      });
      const { error: rapproErr } = await supabase.from("facture_achat_rapprochements").insert(rapprochementPayload);
      if (rapproErr) throw rapproErr;

      await genererEcritureFactureAchatValidee(facture.id);
      showToast("success", "Facture créée et transaction rapprochée ✓");
      setFactureDraft(null);
      onCreatedAndLinked?.();
    } catch (err) {
      const error = err;
      const sqlErr = err as { message?: string; code?: string; details?: string; hint?: string } | null;
      console.error("[piece-first] full error:", JSON.stringify(error, null, 2));
      console.error("[piece-first] create/link SQL error", {
        message: sqlErr?.message,
        code: sqlErr?.code,
        details: sqlErr?.details,
        hint: sqlErr?.hint,
        raw: err,
      });
      showToast("error", err instanceof Error ? err.message : "Création/rapprochement impossible.");
    } finally {
      setCreatingFacture(false);
    }
  };

  return (
    <section style={{ marginBottom: 16 }}>
      <h3
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#0f172a",
          marginBottom: 8,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        Pièce first
      </h3>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf,image/*"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {hasPiece ? (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 12 }}>
          {isPdfType(piece_type) ? (
            <a
              href={displayUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 14px",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                textDecoration: "none",
                color: "#0f172a",
                maxWidth: "100%",
                flex: "1 1 200px",
                cursor: "pointer",
              }}
            >
              <FileText size={28} strokeWidth={1.75} color="#64748b" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {piece_nom || "Document PDF"}
              </span>
            </a>
          ) : isImageType(piece_type) ? (
            <a
              href={displayUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "block", borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0", lineHeight: 0 }}
            >
              <img src={displayUrl || ""} alt={piece_nom || "Pièce"} style={{ maxHeight: 120, maxWidth: "100%", display: "block", objectFit: "contain" }} />
            </a>
          ) : (
            <a
              href={displayUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 14px",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                textDecoration: "none",
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <FileText size={24} color="#64748b" />
              {piece_nom || "Ouvrir la pièce"}
            </a>
          )}

          <button
            type="button"
            disabled={uploading}
            onClick={triggerFilePick}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: uploading ? "#f8fafc" : "white",
              color: "#475569",
              fontSize: 12,
              fontWeight: 600,
              cursor: uploading ? "not-allowed" : "pointer",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              alignSelf: "center",
            }}
          >
            {uploading ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                Envoi…
              </span>
            ) : (
              "Remplacer la pièce"
            )}
          </button>
        </div>
      ) : (
        <div
          style={{
            border: "2px dashed #cbd5e1",
            borderRadius: 12,
            padding: "14px 12px",
            textAlign: "center",
            background: "#fff",
          }}
        >
          <Upload size={32} strokeWidth={1.5} color="#94a3b8" style={{ margin: "0 auto 10px", display: "block" }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b", marginBottom: 10 }}>Aucune pièce importée</div>
          <button
            type="button"
            disabled={uploading}
            onClick={triggerFilePick}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: uploading ? "#fdba74" : "#FF9500",
              color: "white",
              fontSize: 12,
              fontWeight: 600,
              cursor: uploading ? "not-allowed" : "pointer",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            }}
          >
            {uploading ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                Envoi…
              </span>
            ) : (
              "Importer une facture"
            )}
          </button>
        </div>
      )}

      {ocrLoading && (
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, color: "#475569", fontSize: 12 }}>
          <Loader2 size={14} className="animate-spin" />
          Analyse en cours...
        </div>
      )}

      {factureDraft && !ocrLoading && (
        <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 10, padding: 12, background: "#fff" }}>
          <div style={{ display: "grid", gap: 8 }}>
            <label style={{ fontSize: 11, color: "#6b7280" }}>
              Fournisseur
              <input
                value={factureDraft.fournisseur_nom}
                onChange={(e) => handleDraftChange({ fournisseur_nom: e.target.value })}
                onBlur={() => void handleResolveSupplier()}
                style={{ width: "100%", marginTop: 4, padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
              />
            </label>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 11, color: fournisseurConnu ? "#15803d" : "#9ca3af", fontWeight: 600 }}>
                {fournisseurConnu ? "Fournisseur connu" : "Fournisseur inconnu"}
              </span>
              {!fournisseurConnu && (
                <button
                  type="button"
                  disabled={creatingFournisseur}
                  onClick={() => void handleCreateSupplier()}
                  style={{ padding: "5px 8px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", fontSize: 11, color: "#374151", cursor: "pointer" }}
                >
                  {creatingFournisseur ? "Création..." : "Créer le fournisseur"}
                </button>
              )}
            </div>
            <label style={{ fontSize: 11, color: "#6b7280" }}>
              Numéro facture
              <input
                value={factureDraft.numero}
                onChange={(e) => handleDraftChange({ numero: e.target.value })}
                style={{ width: "100%", marginTop: 4, padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
              />
            </label>
            <label style={{ fontSize: 11, color: "#6b7280" }}>
              Date d'émission
              <input
                type="date"
                value={factureDraft.date_emission}
                onChange={(e) => handleDraftChange({ date_emission: e.target.value })}
                style={{ width: "100%", marginTop: 4, padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
              />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <label style={{ fontSize: 11, color: "#6b7280" }}>
                Montant HT
                <input
                  type="number"
                  value={Number.isFinite(factureDraft.montant_ht) ? factureDraft.montant_ht : 0}
                  onChange={(e) => handleDraftChange({ montant_ht: Number(e.target.value) || 0 })}
                  style={{ width: "100%", marginTop: 4, padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                />
              </label>
              <label style={{ fontSize: 11, color: "#6b7280" }}>
                TVA
                <input
                  type="number"
                  value={Number.isFinite(factureDraft.montant_tva) ? factureDraft.montant_tva : 0}
                  onChange={(e) => handleDraftChange({ montant_tva: Number(e.target.value) || 0 })}
                  style={{ width: "100%", marginTop: 4, padding: "7px 9px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                />
              </label>
            </div>
            <div style={{ fontSize: 12, color: "#111827", fontWeight: 600, padding: "6px 8px", background: "#f9fafb", borderRadius: 8 }}>
              TTC: {fmt(ttcForm)}
            </div>
            <button
              type="button"
              disabled={creatingFacture}
              onClick={() => void handleCreateFactureAndLink()}
              style={{ width: "100%", padding: "9px 12px", border: "none", borderRadius: 8, background: "#FF9500", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
            >
              {creatingFacture ? "Création en cours..." : "Créer la facture et rapprocher"}
            </button>
            <button
              type="button"
              onClick={() => setFactureDraft(null)}
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", color: "#4b5563", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function TransactionComptaDrawer({
  open,
  onClose,
  transaction,
  dossierId,
  onRapprochement,
  onCommentairesChanged,
  onPieceUpdated,
  onJustificatifUpdated,
}: TransactionComptaDrawerProps) {
  const [activeTab, setActiveTab] = useState<"ecritures" | "reconciliation" | "commentaires" | "activite">("ecritures");
  const [commentairesCount, setCommentairesCount] = useState(0);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [localPiece, setLocalPiece] = useState({
    nom: null as string | null,
    url: null as string | null,
    type: null as string | null,
    taille: null as number | null,
  });
  const [locJustif, setLocJustif] = useState<{ requis: boolean; statut: JustificatifStatut }>({
    requis: true,
    statut: "en_attente",
  });
  const [justifSaving, setJustifSaving] = useState(false);

  useEffect(() => {
    setLocalPiece({
      nom: transaction.piece_nom ?? null,
      url: transaction.piece_url ?? null,
      type: transaction.piece_type ?? null,
      taille: transaction.piece_taille ?? null,
    });
  }, [
    transaction.id,
    transaction.piece_nom,
    transaction.piece_url,
    transaction.piece_type,
    transaction.piece_taille,
  ]);

  useEffect(() => {
    setLocJustif({
      requis: transaction.justificatif_requis !== false,
      statut: normalizeJustificatifStatut(transaction.justificatif_statut ?? undefined),
    });
  }, [transaction.id, transaction.justificatif_requis, transaction.justificatif_statut]);

  useEffect(() => {
    if (!open || !transaction.id || !dossierId) {
      setCommentairesCount(0);
      return;
    }
    let cancelled = false;
    countCommentaires("transaction", transaction.id, dossierId)
      .then((n) => {
        if (!cancelled) setCommentairesCount(n);
      })
      .catch(() => {
        if (!cancelled) setCommentairesCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [open, transaction.id, dossierId]);

  const pieceUrl = (localPiece.url ?? transaction.piece_url)?.trim();
  const hasPiece = Boolean(pieceUrl);
  const ctx = transaction.context ?? "direct";
  const hasRapprochementContext =
    ctx === "achat" || ctx === "vente_encaissement" || ctx === "vente_remboursement";
  const isTresorerieDirecte = transaction.modeTraitement === "tresorerie_directe";

  let statutLabel: string;
  let badgeBg: string;
  let badgeColor: string;
  if (
    transaction.statut_justification === "justifiee" ||
    (isTresorerieDirecte && locJustif.statut === "non_requis")
  ) {
    statutLabel = "Justifiée";
    badgeBg = "#f0fdf4";
    badgeColor = "#15803d";
  } else if (hasRapprochementContext) {
    statutLabel = "Justifiée";
    badgeBg = "#f0fdf4";
    badgeColor = "#15803d";
  } else if (hasPiece) {
    statutLabel = "Pièce jointe";
    badgeBg = "#eff6ff";
    badgeColor = "#1d4ed8";
  } else {
    statutLabel = "Non justifiée";
    badgeBg = "#fef2f2";
    badgeColor = "#b91c1c";
  }

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), type === "error" ? 5000 : 3500);
  };

  const handlePieceUpdated: TransactionComptaDrawerProps["onPieceUpdated"] = (patch) => {
    setLocalPiece({
      nom: patch.piece_nom,
      url: patch.piece_url,
      type: patch.piece_type,
      taille: patch.piece_taille,
    });
    if (patch.justificatif_statut) {
      setLocJustif((j) => ({ ...j, statut: patch.justificatif_statut! }));
    }
    onPieceUpdated?.(patch);
  };

  const switchJustifRequis = locJustif.statut !== "non_requis";

  const handleToggleJustificatifRequis = async (requis: boolean) => {
    if (!isTresorerieDirecte || justifSaving) return;
    setJustifSaving(true);
    try {
      let statut: JustificatifStatut;
      let req = requis;
      if (!requis) {
        statut = "non_requis";
        req = false;
      } else {
        const hasPiece = Boolean((localPiece.url ?? transaction.piece_url)?.trim());
        statut = hasPiece ? "fourni" : "en_attente";
        req = true;
      }
      const { statut_justification } = await updateTransactionJustificatifCompta(transaction.id, {
        justificatif_requis: req,
        justificatif_statut: statut,
      });
      setLocJustif({ requis: req, statut });
      onJustificatifUpdated?.({
        justificatif_requis: req,
        justificatif_statut: statut,
        ...(statut_justification !== undefined ? { statut_justification } : {}),
      });
      showToast("success", requis ? "Justificatif requis — la Gestion sera notifiée." : "Aucun justificatif requis pour cette opération.");
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Mise à jour impossible.");
    } finally {
      setJustifSaving(false);
    }
  };

  return (
    <ComptaDrawer open={open} onClose={onClose} width={560}>
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-shrink-0 border-b border-[#e4e8ef] bg-white px-5 py-4">
          <div className="mb-2 flex justify-end">
            <Button type="button" variant="ghost" size="sm" className="!p-1.5" onClick={onClose} aria-label="Fermer">
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div
            className={cn(
              "mb-1 text-2xl font-bold tabular-nums",
              transaction.type === "entree" ? "text-green-600" : "text-red-600"
            )}
          >
            {transaction.type === "entree" ? "+ " : "−"}
            {fmt(transaction.montantTTC)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[15px] font-semibold text-slate-900">{transaction.libelle}</div>
            <div className="text-[12px] text-slate-400">{fmtDate(transaction.date)}</div>
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ background: badgeBg, color: badgeColor }}
            >
              {statutLabel}
            </span>
          </div>
        </div>

        {isTresorerieDirecte ? (
          <div
            style={{
              margin: "0 16px 12px",
              padding: 12,
              background: "#fff",
              borderRadius: 12,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              border: "1px solid #e5e7eb",
            }}
          >
            <div style={{ flex: "1 1 200px" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>Pièce justificative (trésorerie directe)</div>
              <p style={{ fontSize: 12, color: "#64748b", margin: "6px 0 0", lineHeight: 1.45 }}>
                {locJustif.statut === "non_requis" && "Aucun justificatif requis pour cette opération."}
                {locJustif.statut === "en_attente" && "En attente de justificatif côté Gestion."}
                {locJustif.statut === "fourni" && "Justificatif fourni."}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: "#64748b" }}>Justificatif requis</span>
              <button
                type="button"
                role="switch"
                aria-checked={switchJustifRequis}
                disabled={justifSaving}
                onClick={() => handleToggleJustificatifRequis(!switchJustifRequis)}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 999,
                  border: "none",
                  cursor: justifSaving ? "wait" : "pointer",
                  background: switchJustifRequis ? "#FF9500" : "#cbd5e1",
                  position: "relative",
                  transition: "background 0.2s",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 3,
                    left: switchJustifRequis ? 22 : 3,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "white",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    transition: "left 0.2s",
                  }}
                />
              </button>
            </div>
          </div>
        ) : null}

        <div className={cn(formTabsListClass, "!mb-0 flex-shrink-0 bg-white px-2")}>
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={cn(
                formTabButtonClass,
                activeTab === t.id && formTabButtonActiveClass,
                "inline-flex items-center gap-1.5"
              )}
            >
              <t.icon size={14} className="shrink-0" />
              {t.label}
              {t.id === "commentaires" && commentairesCount > 0 && (
                <span className="ml-1 inline-flex min-h-4 min-w-[15px] items-center justify-center rounded-full bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700">
                  {commentairesCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto bg-white px-5 py-4">
          {activeTab === "ecritures" && (
            <TransactionEcrituresTab transactionId={transaction.id} txType={transaction.type} />
          )}
          {activeTab === "reconciliation" && (
            <>
              <TransactionPieceJustificativeSection
                dossierId={dossierId}
                transactionId={transaction.id}
                txDate={transaction.date}
                txMontantTTC={transaction.montantTTC}
                piece_nom={localPiece.nom}
                piece_url={localPiece.url}
                piece_type={localPiece.type}
                piece_taille={localPiece.taille}
                tresorerieDirecte={isTresorerieDirecte}
                onPieceUpdated={handlePieceUpdated}
                onCreatedAndLinked={() => {
                  onRapprochement?.();
                  setActiveTab("ecritures");
                }}
                showToast={showToast}
              />
              <TransactionReconciliationTab
                transactionId={transaction.id}
                txType={transaction.type}
                montantTTC={transaction.montantTTC}
                date={transaction.date}
                libelle={transaction.libelle}
                suggestedTier={transaction.suggestedTier}
                modeTraitement={transaction.modeTraitement as "facture" | "tresorerie_directe" | undefined}
                dossierId={dossierId}
                onRapprochement={onRapprochement}
              />
            </>
          )}
          {activeTab === "commentaires" && (
            <div style={{ minHeight: 420, maxHeight: "70vh", display: "flex", flexDirection: "column" }}>
              <CommentairesTab
                dossierId={dossierId}
                entiteType="transaction"
                entiteId={transaction.id}
                onCommentsChanged={() => {
                  void countCommentaires("transaction", transaction.id, dossierId).then(setCommentairesCount);
                  onCommentairesChanged?.(transaction.id);
                }}
              />
            </div>
          )}
          {activeTab === "activite" && (
            <div style={{ padding: 24, textAlign: "center", color: "#475569", fontSize: 14 }}>
              Activité — à venir
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 10050,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px",
            background: "white",
            borderRadius: 12,
            boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.08)",
            border: toast.type === "error" ? "1px solid #fecaca" : "1px solid #bbf7d0",
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            fontSize: "0.9rem",
            fontWeight: 600,
            color: "#0f172a",
            maxWidth: 360,
          }}
        >
          {toast.type === "success" ? (
            <CheckCircle size={24} strokeWidth={2} color="#16A34A" style={{ flexShrink: 0 }} />
          ) : (
            <AlertCircle size={24} strokeWidth={2} color="#dc2626" style={{ flexShrink: 0 }} />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </ComptaDrawer>
  );
}
